/**
 * ImportTab -- V2 AI Workspace import panel.
 *
 * Redesigned 2026-08-25. It used to be the one selection-agnostic tab: a drop zone, a preview of
 * the IMPORTED file, a grid that never highlighted anything, and a tap-a-shot confirm popup that
 * wrote straight to the timeline with no before/after and no way back.
 *
 * It now works like Create and Touch Up: the shared workspace selection names the target, the
 * preview shows THAT SHOT (never the dropped file), and dropping hands the data URL to the flow's
 * `startImportReview`, which parks it in the ordinary generation review — before/after, Discard,
 * Apply. The confirm popup is gone with it.
 *
 * Import always needs a target, so the grid tap is a plain select, not the toggle Create and Touch
 * Up use: tapping the already-selected shot is a no-op rather than a deselect.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { ShotGrid } from './ShotGrid.tsx';
import { WorkspaceShotPreview } from './WorkspaceShotPreview.tsx';
import { ImageFlyOverlay } from '@/components/ImageFlyOverlay.tsx';
import { useProjectStore } from '@/store/projectStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { showToast } from '@/components/Toast.tsx';
import type { TimelineShot } from '@/types/timeline.ts';
import styles from './AiWorkspace.module.css';

const ACCEPTED = 'image/png,image/jpeg,image/webp,image/gif';
const ACCEPTED_TYPES = /\.(png|jpe?g|webp|gif)$/i;

interface Props {
  /** The shared workspace selection's shot — the import target, and what the preview shows. */
  effectiveShot: TimelineShot | null;
  /** Grid tap → make this the single shared selection. */
  onSelectShot: (shotId: number) => void;
  /** A file landed: hand its data URL to the flow, which opens the before/after review. */
  onStartImportReview: (dataUrl: string) => void;
}

export function ImportTab({ effectiveShot, onSelectShot, onStartImportReview }: Props) {
  const { t } = useTranslation('aiWorkspace');
  const isMobile = useMobileLayout();

  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Fullscreen preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewShotId, setPreviewShotId] = useState<number | null>(null);
  const previewSourceEl = useRef<HTMLElement | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  // Project data
  const project = useProjectStore(s => s.project);
  const videoShots = useProjectStore(s => s.videoShots);
  const allScenes = project?.scenes ?? [];
  const separator = project?.documentSettings?.separator ?? '';
  const allShots = useMemo(
    () => allScenes.flatMap(s => s.shots),
    [allScenes],
  );
  const videoShotIds = useMemo(
    () => new Set(videoShots.map(v => v.shotId)),
    [videoShots],
  );
  const approvals = useCommentStore(s => s.approvals);
  const approvedShotIds = useMemo(
    () => new Set(approvals.filter(a => a.status === 'approved').map(a => a.shotId)),
    [approvals],
  );

  // Highlight the import target, exactly as TouchUpTab highlights its own.
  const selectedIds = useMemo(
    () => effectiveShot ? new Set([effectiveShot.id]) : new Set<number>(),
    [effectiveShot],
  );

  // -- File handling --

  /**
   * The target is checked BEFORE the read: a multi-megabyte FileReader pass whose only possible
   * outcome is an error toast is wasted work, and the toast should land on the drop, not a tick
   * later.
   */
  const handleFile = useCallback((file: File) => {
    if (!effectiveShot) {
      showToast(t('importNoShotSelected'), 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onStartImportReview(reader.result as string);
    };
    reader.onerror = () => {
      console.error('[ImportTab] Failed to read file:', reader.error);
    };
    reader.readAsDataURL(file);
  }, [effectiveShot, onStartImportReview, t]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }, [handleFile]);

  // Files-only, the same gate every other drop site in the app runs (useShotImageDrop.ts). Without
  // it a text or HTML drag arms the zone and the user gets a dashed accent outline for a drag that
  // can never drop.
  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  }, []);

  // dragleave bubbles from CHILD boundaries too (the hint text, the <span>, the hidden input), so
  // clearing unconditionally makes the outline flicker on every internal crossing.
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || ACCEPTED_TYPES.test(f.name),
    );
    // Single file, as shipped: N files would mean N review items, which flips the review out of its
    // single-item layout and back into per-item accept/reject chrome.
    if (files[0]) handleFile(files[0]);
  }, [handleFile]);

  // -- Grid tap: retarget the shared selection --

  const handleGridTap = useCallback((shotId: number) => {
    // No deselect branch. Create and Touch Up can fall back to `currentShot` when their override is
    // nulled; Import IS the shared selection, so a toggle here would leave it with no target and
    // the next drop would only be able to toast.
    if (shotId === effectiveShot?.id) return;
    onSelectShot(shotId);
  }, [effectiveShot, onSelectShot]);

  // -- Grid preview: long-press / double-click --

  const handleGridPreview = useCallback((shotId: number, cellEl: HTMLElement) => {
    previewSourceEl.current = cellEl;
    setPreviewShotId(shotId);
    setPreviewOpen(true);
  }, []);

  // ImageFlyOverlay items
  const previewItems = useMemo(
    () => allShots.filter(s => s.thumbnailDataUrl).map(s => ({ url: s.thumbnailDataUrl! })),
    [allShots],
  );
  const shotsWithThumbs = useMemo(
    () => allShots.filter(s => s.thumbnailDataUrl),
    [allShots],
  );
  const previewActiveIndex = useMemo(() => {
    if (previewShotId == null) return 0;
    const idx = shotsWithThumbs.findIndex(s => s.id === previewShotId);
    return idx >= 0 ? idx : 0;
  }, [previewShotId, shotsWithThumbs]);

  const handlePreviewIndexChange = useCallback((newIndex: number) => {
    const shot = shotsWithThumbs[newIndex];
    if (shot) setPreviewShotId(shot.id);
  }, [shotsWithThumbs]);

  return (
    <div ref={mainRef} className={styles.main} style={{ gap: 8 }}>
      {/* Drop zone + selected-shot preview */}
      <div className={styles.dropdownRow}>
        <label
          className={`${styles.dropZone} ${dragging ? styles.dropZoneActive : ''}`}
          data-file-drop-target=""
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {t('importDropHint')}
          <br />
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{t('importDropFormats')}</span>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </label>

        {/* The SELECTED SHOT, not the dropped file — the file gets its own before/after review. */}
        <div className={styles.previewFloat}>
          <WorkspaceShotPreview shot={effectiveShot} />
        </div>
      </div>

      {/* ── Divider ── */}
      <div className={styles.gridDivider} />

      {/* ── Shot Grid ── */}
      <ShotGrid
        shots={allShots}
        scenes={allScenes}
        separator={separator}
        selectedIds={selectedIds}
        videoShotIds={videoShotIds}
        approvedShotIds={approvedShotIds}
        onTap={handleGridTap}
        onPreview={handleGridPreview}
        {...(isMobile ? { scrollParent: mainRef.current } : {})}
      />

      {/* ── Fullscreen Preview Overlay ── */}
      <AnimatePresence>
        {previewOpen && previewItems.length > 0 && (
          <ImageFlyOverlay
            key="import-preview"
            activeIndex={previewActiveIndex}
            items={previewItems}
            sourceElement={previewSourceEl.current}
            onClose={() => setPreviewOpen(false)}
            onIndexChange={handlePreviewIndexChange}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
